import React, { useCallback, useEffect, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { useRouter } from 'expo-router';
import { DatadogSessionReplayMask } from 'features/observability/rum/datadog-session-replay-mask';
import { Pressable, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Box, Typography } from 'components/ui';
import type { TypographyProps } from 'components/ui';
import { LogUserErrorSeverity, logUserErrorMessage } from 'hooks/log-user-error-message';
import useEffectOnce from 'hooks/use-effect-once';
import { HttpErrorCodes } from 'remote/constants';
import { useAuthContext } from 'state/auth';
import {
  getStoredOtpCredentials,
  storeOtpCredentials,
} from 'state/auth/hooks/use-account-authentication';
import { ModalAuthScreen } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { getDesignToken, primitive, spacing } from 'styles/constants';
import { isWeb } from 'utils/environment';
import logger from 'utils/logger';
import { TraceName, stopTrace } from 'utils/performance/trace';
import { routes } from 'utils/routing';

import { OtpForm } from './otp-form';
import { useResendOtp } from './use-resend-otp.hook';
import { useSendNewOtp } from './use-send-new-otp.hook';

// Original styling bumped these texts up one step on web only. Preserved here.
const WEB_FONT_SIZE_OVERRIDE: TypographyProps['fontSize'] = isWeb ? 'sm' : undefined;

interface GraphQLError {
  graphQLErrors?: Array<{
    extensions?: {
      statusCode?: number;
    };
  }>;
}

export const ConfirmOtp = () => {
  const { bottom: safeAreaBottom } = useSafeAreaInsets();
  const {
    isAuthenticated,
    originLocation,
    setOriginLocation,
    validateLoginOtp,
    setOtpAuthError,
    modalAuthIsOpen,
    setModalAuthState,
    onSignInSuccess,
  } = useAuthContext();

  const client = useApolloClient();

  const { navigate } = useRouter();
  const { email, sessionId } = getStoredOtpCredentials() || {};
  const [isValidatingOtp, setIsValidatingOtp] = useState(false);
  const [showSendNewOtpForm, setShowSendNewOtpForm] = useState(false);

  const [otpValidationError, setOtpValidationError] = useState<string | null>(null);
  const [resendOrSendNewOtpError, setResendOrSendNewOtpError] = useState<string | null>(null);
  const [showHasResentOtpMessage, setShowHasSentResentOtpMessage] = useState(false);
  const [showHasSentNewOtpMessage, setShowHasSentNewOtpMessage] = useState(false);

  const hasEmail = Boolean(email?.length);

  const { sendNewOtp, isLoading: isRequestingNewCode } = useSendNewOtp({
    setOtpValidationError,
    setOtpAuthError,
    setResendOrSendNewOtpError,
    setShowHasSentNewOtpMessage,
    setShowSendNewOtpForm,
  });

  const { resendOtp, isLoading: isResendingOtp } = useResendOtp({
    setOtpValidationError,
    setResendOrSendNewOtpError,
    setShowHasSentResentOtpMessage,
    setShowSendNewOtpForm,
  });

  // Either if we have a login, or once we validate OTP and get an update for the user being loaded
  // we can then handle the navigate.
  useEffect(() => {
    // If the confirm OTP is rendered in the ModalAuth component, we don't want to navigate away from the modal.
    if (modalAuthIsOpen) {
      return;
    }

    if (isAuthenticated) {
      const path = originLocation || routes.base;
      setOriginLocation(null);
      navigate({
        pathname: path,
        // This allows us to notify the screen reader to announce a successful sign in only when signing in via OTP.
        params: {
          triggerSignInAccessibility: 1,
        },
      });
    }
  }, [isAuthenticated, originLocation, modalAuthIsOpen, navigate, setOriginLocation]);

  const handleValidateLogin = useCallback(
    async (otpCode: string) => {
      setIsValidatingOtp(true);
      setShowHasSentNewOtpMessage(false);
      setOtpValidationError(null);
      setOtpAuthError(null);
      setResendOrSendNewOtpError(null);

      try {
        await validateLoginOtp({ otpCode });
        // refetch offers when user navigates to /offers page
        client.reFetchObservableQueries();
        onSignInSuccess();
        stopTrace(TraceName.OTP_FLOW);
      } catch (error) {
        let errorMessage;
        setIsValidatingOtp(false);

        if (!sessionId) {
          errorMessage = logUserErrorMessage({
            message: t('differentDeviceError'),
            severity: LogUserErrorSeverity.Recoverable,
          });

          setShowSendNewOtpForm(true);
          setOtpValidationError(errorMessage);
          setOtpAuthError(errorMessage);
          throw error;
        }

        const graphQLError = error as GraphQLError;
        switch (graphQLError?.graphQLErrors?.[0]?.extensions?.statusCode) {
          case HttpErrorCodes.InternalServerError:
            errorMessage = logUserErrorMessage({
              message: t('looksLikeWereExperiencingIssues'),
              severity: LogUserErrorSeverity.Irrecoverable,
            });
            setShowSendNewOtpForm(false);
            break;
          case HttpErrorCodes.TooManyRequests:
            errorMessage = logUserErrorMessage({
              message: t('maxAttemptsReachedRequestNewCode'),
              severity: LogUserErrorSeverity.Recoverable,
            });
            setShowSendNewOtpForm(true);
            break;
          case HttpErrorCodes.Unauthorized:
          default:
            errorMessage = logUserErrorMessage({
              message: t('invalidOtpProvided'),
              severity: LogUserErrorSeverity.Recoverable,
            });
            setShowSendNewOtpForm(true);
            break;
        }
        setOtpValidationError(errorMessage);
        setOtpAuthError(errorMessage);
        throw error;
      }
    },
    [setOtpAuthError, validateLoginOtp, client, onSignInSuccess, sessionId]
  );

  useEffectOnce(() => {
    if (!email || !sessionId) {
      if (email && !sessionId) {
        logger.error(
          new Error(`Missing sessionId required to store OTP credentials. hasEmail: true`)
        );
      }
      setModalAuthState({
        screen: ModalAuthScreen.SIGN_IN,
      });
      return;
    }

    storeOtpCredentials({ sessionId, email });
  });

  return (
    <Box style={{ paddingBottom: safeAreaBottom }}>
      <Typography variant="headerTwo" textAlign="left" style={styles.headline}>
        {t('authVerifyWithCode')}
      </Typography>
      {hasEmail && (
        <DatadogSessionReplayMask>
          <Typography
            aria-label={t('visuallyHiddenSignInCodeSentToEmail')}
            color={getDesignToken('text-default')}
            fontSize={WEB_FONT_SIZE_OVERRIDE}
            variant="copyOne"
          >
            {t('authEmailSentEmail')}
            &nbsp;
            <Typography testID="otp-email" data-private={true}>
              {email}
            </Typography>
          </Typography>
        </DatadogSessionReplayMask>
      )}

      <OtpForm
        errorMessage={otpValidationError}
        loading={isValidatingOtp}
        onSubmit={handleValidateLogin}
      />

      {showSendNewOtpForm && (
        <Pressable
          disabled={isRequestingNewCode || isValidatingOtp}
          onPress={sendNewOtp}
          style={styles.sendNewCodeButton}
          testID="otp-send-new-form"
        >
          <Typography
            bold
            color={getDesignToken('text-default')}
            fontSize={WEB_FONT_SIZE_OVERRIDE}
            style={styles.pressableText}
            variant="copyTwo"
          >
            {t('sendNewCode')}
          </Typography>
        </Pressable>
      )}

      {showHasSentNewOtpMessage && hasEmail && (
        <Typography
          aria-live="polite"
          style={styles.codeSentText}
          textAlign="left"
          variant="copyOne"
        >
          {t('newCodeSent')}
        </Typography>
      )}

      {/** resend OTP & send new OTP forms are never visible at the same time, they alternate */}
      {!showSendNewOtpForm && (
        <>
          <Box style={[styles.notReceived, isWeb && styles.notReceivedWeb]}>
            <Typography fontSize={WEB_FONT_SIZE_OVERRIDE} style={styles.codeText} variant="copyTwo">
              {t('didNotReceiveCodeHeading')}
            </Typography>
            <Pressable
              disabled={isValidatingOtp || isResendingOtp}
              onPress={resendOtp}
              testID="otp-resend-form"
            >
              <Typography
                bold
                color={getDesignToken('text-default')}
                fontSize={WEB_FONT_SIZE_OVERRIDE}
                style={styles.pressableText}
                variant="copyTwo"
              >
                {t('resendCode')}
              </Typography>
            </Pressable>
          </Box>
          {!showHasSentNewOtpMessage && showHasResentOtpMessage && hasEmail && (
            <Typography
              aria-live="polite"
              style={styles.codeSentText}
              testID="otp-resend-success"
              textAlign="left"
              variant="copyOne"
            >
              {t('signInCodeSentToEmailAgain')}
            </Typography>
          )}
        </>
      )}

      {resendOrSendNewOtpError && (
        <Typography
          accessibilityRole="alert"
          color={primitive.$error}
          style={styles.errorMessage}
          testID="otp-resend-or-send-new-error"
          variant="copyTwo"
        >
          {resendOrSendNewOtpError}
        </Typography>
      )}
    </Box>
  );
};

export default ConfirmOtp;

const styles = StyleSheet.create({
  codeSentText: {
    marginHorizontal: 0,
    marginVertical: 3,
  },
  codeText: {
    margin: 0,
    paddingBottom: 1.25,
    paddingHorizontal: 1,
    paddingTop: 0,
  },
  errorMessage: {
    marginBottom: 6,
    marginHorizontal: 0,
    marginTop: spacing.$2,
  },
  headline: {
    marginBottom: spacing.$4,
    marginHorizontal: 0,
    marginTop: 0,
  },
  notReceived: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    margin: 0,
  },
  notReceivedWeb: {
    marginTop: spacing.$2,
  },
  pressableText: {
    margin: 0,
    textDecorationLine: 'underline',
  },
  sendNewCodeButton: {
    alignSelf: 'center',
    marginTop: spacing.$4,
  },
});
